'use client'

import { useCallback } from 'react'
import { useLocale } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'

import { storePostAuthRedirectPath } from '@/services/auth/clientAuthState'
import { buildLocalizedPublicLoginHref } from '@/services/auth/postAuth.client'
import { sanitizeRedirectPath } from '@/services/auth/session.client'
import { performLocalSessionCleanup } from '@/services/auth/logoutCleanup.client'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'

let logoutInProgress = false

function getCurrentBrowserRedirectPath(): string {
  if (typeof window === 'undefined') return '/'

  return sanitizeRedirectPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    '/',
  )
}

export type LogoutReason = 'manual' | 'inactivity' | 'refresh_failure'

export function useSessionOrchestrator() {
  const queryClient = useQueryClient()
  const locale = useLocale()

  const beginLogout = useCallback(
    async (
      reason: LogoutReason,
      options?: {
        skipSignOut?: boolean
      },
    ): Promise<void> => {
      if (logoutInProgress) return
      logoutInProgress = true
      const shouldPreserveCurrentPath = reason !== 'manual'
      const redirectPath = shouldPreserveCurrentPath
        ? getCurrentBrowserRedirectPath()
        : '/'

      captureSessionEvent('logout_started', reason)

      try {
        const defaultOptions = queryClient.getDefaultOptions()
        queryClient.setDefaultOptions({
          ...defaultOptions,
          queries: {
            ...defaultOptions.queries,
            retry: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
          },
        })

        await performLocalSessionCleanup(queryClient, {
          skipSignOut: options?.skipSignOut,
          clearPersistPreference: true,
          clearAppCookies: true,
          clearLegacyAuthState: true,
        })
        captureSessionEvent('logout_completed', reason)
      } finally {
        if (redirectPath !== '/') {
          storePostAuthRedirectPath(redirectPath)
        }
        const localizedLoginPath = shouldPreserveCurrentPath
          ? buildLocalizedPublicLoginHref(redirectPath)
          : `/${locale}/login`
        setTimeout(() => {
          logoutInProgress = false
        }, 2000)
        window.location.replace(localizedLoginPath)
      }
    },
    [locale, queryClient],
  )

  return { beginLogout }
}
